// useResponsibilityChainSave — the ADR-052 confirm-on-save backing flow (the
// scope grant; on a team, the membership — ADR-083 §3), shared by every chain
// stage (`useChainEdit` in ChainsDrawer, at every level) and the leave's
// handover. This is the single home for the flow so a new level cannot silently
// ship without it (the zone regression: a hardcoded
// grant_backing:false + a drawer with no 409 handling meant zone accountability
// could never grant the backing scope).
//
// The flow: a first save omits grant_backing; a 409 ASSIGNEE_NOT_BACKED does NOT
// error — it surfaces a confirm naming the unbacked assignees; confirming re-saves
// with grant_backing=true (the companion scope grant, same tx). Any other refusal is
// the host's line when it holds one (`onRefused` — the chain drawer's, ADR-084 §6),
// else a string through errors.json (the leave's and the hand-over's dialogs).
//
// The PUT is injected as `submit(grantBacking)` (the host builds it over the one
// `useSetAssignmentChain` wire + its current draft), so this hook stays level-agnostic.

import { useState } from 'react';
import type { TFunction } from 'i18next';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '../lib/apiError.js';

export interface ResponsibilityChainSave {
  /** Run the save (grant_backing=false on the first attempt). */
  save: () => void;
  /** The unbacked assignee ids the last save surfaced — the backing confirm is open
   *  while this is non-null. Empty array = the confirm is open but no names resolved. */
  backingPrompt: string[] | null;
  /** Confirm the backing grant → re-save with grant_backing=true. */
  confirmBacking: () => void;
  /** Dismiss the backing confirm without granting. */
  dismissBacking: () => void;
  /** A mapped error string for a non-backing failure, or null. */
  error: string | null;
  /** Clear the error banner. */
  clearError: () => void;
  /** Reset all transient state (call from the drawer's open/close reset). */
  reset: () => void;
}

export function useResponsibilityChainSave(opts: {
  /** The level-specific PUT — resolves on success, rejects with the Axios error. */
  submit: (grantBacking: boolean) => Promise<unknown>;
  /** Called once a save (or a confirmed backing grant) succeeds. */
  onSaved: () => void;
  /** Called when a save fails for any reason but the backing consent — the mapped message is in `error` by then
   *  (a host that holds its own busy flag across a two-step save releases it here). */
  onError?: (message: string) => void;
  /** A host with its own refusal line takes the refusal itself (its words, the stale line's Load the latest);
   *  `error` then stays null. */
  onRefused?: (err: unknown) => void;
  /** The `errors` namespace translator (maps the backend `code`). */
  tErrors: TFunction;
}): ResponsibilityChainSave {
  const [backingPrompt, setBackingPrompt] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (grantBacking: boolean): Promise<void> => {
    setError(null);
    try {
      await opts.submit(grantBacking);
      setBackingPrompt(null);
      opts.onSaved();
    } catch (e) {
      if (apiErrorCode(e) === 'ASSIGNEE_NOT_BACKED') {
        // Surface the confirm rather than an error — the operator may grant backing.
        const unbacked = apiErrorDetails(e).unbacked;
        setBackingPrompt(Array.isArray(unbacked) ? (unbacked as string[]) : []);
        return;
      }
      if (opts.onRefused) {
        opts.onRefused(e);
        return;
      }
      const message = apiErrorMessage(e, opts.tErrors);
      setError(message);
      opts.onError?.(message);
    }
  };

  return {
    save: () => void run(false),
    backingPrompt,
    confirmBacking: () => void run(true),
    dismissBacking: () => setBackingPrompt(null),
    error,
    clearError: () => setError(null),
    reset: () => {
      setBackingPrompt(null);
      setError(null);
    },
  };
}
